/**
 * Canonical hash routes for the Trexpert portal shell. German slugs are used
 * for persona-facing top-level paths so customer and trustee navigation share
 * the same vocabulary (`#/dokumente`, `#/aufgaben`). Role-specific resolution
 * happens in `demo-app` `bootstrap.ts`.
 */
export const ROUTES = {
    dashboard: "#/dashboard",
    dokumente: "#/dokumente",
    aufgaben: "#/aufgaben",
    hochladen: "#/hochladen",
    inbox: "#/inbox",
    settings: "#/settings",
    mandates: "#/mandates",
    formulare: "#/formulare",
} as const;

export const customerTaskList = (taskListId: string): string => `${ROUTES.aufgaben}/${taskListId}`;

/** Cross-mandate document overview pre-filtered by lifecycle status (e.g. the
    dashboard «In Review» tile → `#/dokumente/in_review`). */
export const documentsByStatus = (status: string): string => `${ROUTES.dokumente}/${status}`;

/** Customer Dokumente deep-link slugs — resolved to sender/status filters in the
    Mandatsraum document list (oracle: KundeDokumente «neu» tab). */
export const CUSTOMER_DOCUMENT_FILTERS = {
    unread: "neu",
    submitted: "eingereicht",
} as const;

export type CustomerDocumentFilterSlug =
    (typeof CUSTOMER_DOCUMENT_FILTERS)[keyof typeof CUSTOMER_DOCUMENT_FILTERS];

export const isCustomerDocumentFilterSlug = (slug: string): slug is CustomerDocumentFilterSlug =>
    slug === CUSTOMER_DOCUMENT_FILTERS.unread || slug === CUSTOMER_DOCUMENT_FILTERS.submitted;

/** Customer dashboard «Neue Dokumente» tile → unread trustee-provided documents. */
export const customerDocumentsUnread = (): string =>
    `${ROUTES.dokumente}/${CUSTOMER_DOCUMENT_FILTERS.unread}`;

/** Customer dashboard «Eingereichte Dokumente» tile → own uploads. */
export const customerDocumentsSubmitted = (): string =>
    `${ROUTES.dokumente}/${CUSTOMER_DOCUMENT_FILTERS.submitted}`;

/** Mandatsloser Trustee-Upload, gestartet aus der Dokument-Übersicht: das
    upload-document-Touchpoint wird OHNE `mandateId` gemountet, sodass die
    Mandat-Auswahl vorgeschaltet wird. */
export const documentsUpload = (): string => `${ROUTES.dokumente}/hochladen`;

export const mandateWorkspace = (mandateId: string): string => `${ROUTES.mandates}/${mandateId}`;

export const mandateTaskLists = (mandateId: string): string => `${ROUTES.mandates}/${mandateId}/task-lists`;

/** The Mandate's recurring Aufgabenpläne, a sibling section of the lists
    (`#/mandates/:id/task-plans`). Trustee-only — customers have no plans view. */
export const mandateTaskPlans = (mandateId: string): string => `${ROUTES.mandates}/${mandateId}/task-plans`;

export const mandateTaskList = (mandateId: string, taskListId: string): string =>
    `${ROUTES.mandates}/${mandateId}/task-lists/${taskListId}`;

export const mandateUpload = (mandateId: string): string => `${ROUTES.mandates}/${mandateId}/upload`;

/** True when the hash is a customer flat Aufgaben route (list or detail). */
export const isCustomerAufgabenRoute = (hash: string): boolean =>
    hash === ROUTES.aufgaben || /^#\/aufgaben\/[^/]+$/.test(hash);
